"use client";

import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Frown, Smile, Clock, Zap, SearchX, CircleCheck, Brain, Sparkles, TrendingDown, TrendingUp, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  id: 1,
  name: "断句速度",
  tradText: "手动拖拽进度条，定位困难",
  tradIcon: Clock,
  aiText: "AI秒级自动拆解，精准切分",
  aiIcon: Zap
}, {
  id: 2,
  name: "查词效率",
  tradText: "频繁切换词典，打断学习思路",
  tradIcon: SearchX,
  aiText: "即点即查，结合语境精准翻译",
  aiIcon: CircleCheck
}, {
  id: 3,
  name: "复习频率",
  tradText: "缺乏规划，学完就忘效率低",
  tradIcon: Brain,
  aiText: "智能间隔重复算法，牢固记忆",
  aiIcon: Sparkles
}, {
  id: 4,
  name: "学习反馈",
  tradText: "枯燥盲目无反馈，极易放弃",
  tradIcon: TrendingDown,
  aiText: "实时数据统计，看得见的进步",
  aiIcon: TrendingUp
}];
export default function EfficiencyRevolutionTraditionalVsAi() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const tableReveal = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="效率革命传统-vs-ai" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            效率革命：<span className="text-muted-foreground">传统</span> vs{" "}
            <span className="text-primary">AI</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            告别低效、枯燥的传统听力练习方式。听力通利用先进的 AI 技术，
            为您打造智能、有趣且充满成就感的全新学习体验。
          </p>
        </div>

        <div ref={tableReveal.ref} className={`max-w-5xl mx-auto transition-all duration-1000 delay-200 ${tableReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0">
            <Table className="min-w-[800px] w-full border-collapse">
              <TableHeader>
                <TableRow className="border-none hover:bg-transparent">
                  <TableHead className="w-1/4 align-bottom pb-8 text-lg font-semibold text-muted-foreground px-6">
                    对比维度
                  </TableHead>
                  <TableHead className="w-[37.5%] text-center pb-8 px-6">
                    <div className="flex flex-col items-center gap-4 text-muted-foreground">
                      <div className="p-4 rounded-2xl bg-muted/30 border border-border/50">
                        <Frown className="size-8 opacity-70" />
                      </div>
                      <div>
                        <div className="text-xl font-bold text-foreground mb-1">
                          传统听力学习
                        </div>
                        <div className="text-sm opacity-80">
                          枯燥、盲目、易放弃
                        </div>
                      </div>
                    </div>
                  </TableHead>
                  <TableHead className="w-[37.5%] text-center pb-8 px-6 relative">
                    {}
                    <div className="absolute inset-0 bg-primary/10 border-t-2 border-x-2 border-primary/40 rounded-t-[2rem] shadow-[0_-20px_40px_-20px_rgba(var(--color-primary),0.4)] -z-10" />
                    <div className="flex flex-col items-center gap-4 text-primary">
                      <div className="p-4 rounded-2xl bg-primary/20 border border-primary/30 shadow-[0_0_15px_rgba(var(--color-primary),0.3)]">
                        <Smile className="size-8" />
                      </div>
                      <div>
                        <div className="text-xl font-bold mb-1">听力通 AI</div>
                        <div className="text-sm opacity-90">
                          有趣、智能、有反馈
                        </div>
                      </div>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.map((feature, i) => <TableRow key={feature.id} data-index={i} className="border-none hover:bg-transparent group">
                    <TableCell className="py-6 px-6 text-base font-medium text-foreground border-b border-border/20 group-last:border-none">
                      {feature.name}
                    </TableCell>
                    <TableCell className="py-6 px-6 text-center text-muted-foreground border-b border-border/20 group-last:border-none">
                      <div className="flex items-center justify-center gap-2.5">
                        <feature.tradIcon className="size-5 opacity-40" />
                        <span>{feature.tradText}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-6 px-6 text-center font-semibold text-primary relative">
                      {}
                      <div className="absolute inset-0 bg-primary/10 border-x-2 border-primary/40 -z-10" />
                      <div className="flex items-center justify-center gap-2.5">
                        <feature.aiIcon className="size-5" />
                        <span>{feature.aiText}</span>
                      </div>
                    </TableCell>
                  </TableRow>)}
                {}
                <TableRow className="border-none hover:bg-transparent h-8">
                  <TableCell className="p-0"></TableCell>
                  <TableCell className="p-0"></TableCell>
                  <TableCell className="p-0 relative">
                    <div className="absolute inset-0 bg-primary/10 border-b-2 border-x-2 border-primary/40 rounded-b-[2rem] shadow-[0_20px_40px_-20px_rgba(var(--color-primary),0.4)] -z-10" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <div className="mt-12 text-center">
            <Button size="lg" className="rounded-full px-8 h-14 text-base shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-shadow">
              立即开启高效听力之旅
              <ArrowRight className="ml-2 size-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}